import {
  BillingAlertType,
  BillingQueryDocument,
  MeDocument,
  PlanPriceType,
  PlanType,
  PlatformType,
  useBillingQueryQuery,
  useCompleteOnboardingMutation,
  useCreateSubscriptionMutation,
  usePlansQueryQuery,
  useTrackEventMutation,
  type BillingQueryQuery,
  type MeQuery,
  type PlanPriceItem,
} from '@/shared/graphql/legacy/generated'
import { createCardElement, getStripe } from '@/shared/services/stripe'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import { useWebviewStore } from '@/shared/stores/webview'
import { formatPrice } from '@/shared/utils/money'
import { analytics } from '@/shared/services/analytics'
import type { Stripe, StripeCardElement } from '@stripe/stripe-js'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { useChangePlanEstimate } from '@/features/billing/composables/useChangePlanEstimate'

export type { PlanChangeEstimate } from '@/features/billing/composables/useChangePlanEstimate'

const UPGRADE_ALERTS = [
  BillingAlertType.Upgrade,
  BillingAlertType.UpgradeBundle,
]

export function useCheckout() {
  const { result } = usePlansQueryQuery()
  const { mutate: createSubscription } = useCreateSubscriptionMutation()
  const { refetch } = useBillingQueryQuery()
  const { mutate: trackEvent } = useTrackEventMutation()
  const { mutate: completeOnboarding } = useCompleteOnboardingMutation()

  const route = useRoute()
  const router = useRouter()
  const billingStore = useBillingStore()
  const userStore = useUserStore()
  const webviewStore = useWebviewStore()

  const stripe = ref<Stripe | null>(null)
  const card = ref<StripeCardElement | null>(null)
  const acceptTerms = ref(false)
  const loading = ref(false)
  const errorMessage = ref('')

  const selectedPlan = computed(() => billingStore.selectedPlan)
  const selectedPlanItem = computed(() => billingStore.selectedPlanItem)
  const selectedBillingCycle = computed(() => billingStore.selectedBillingCycle)

  const getBillingCyclePrice = (price: PlanPriceItem) => {
    return formatPrice({
      amount: (price.details?.amount || 0) / 100,
      currency: price.details?.currency,
    })
  }

  const hasMoreThanOnePrice = computed(
    () => (selectedPlanItem.value?.prices.length || 0) > 1
  )

  const cardClasses = computed(() => ({
    'pointer-events-none opacity-50': loading.value,
  }))

  const paymentMethod = computed(
    () => billingStore.billing?.paymentMethod?.card
  )

  const { estimate: registrationsEstimate, awaiting: awaitingEstimate } =
    useChangePlanEstimate()

  const setupStripe = async () => {
    stripe.value = await getStripe()
    if (!stripe.value) return
    card.value = createCardElement(stripe.value)
    card.value.on('change', () => {
      errorMessage.value = ''
    })
    card.value?.mount('#card')
  }

  onMounted(async () => {
    if (!paymentMethod.value?.last4) {
      await setupStripe()
    }

    const subscriptionPrice = selectedPlanItem.value?.prices.find(
      (price) => price.type === PlanPriceType.Subscription
    )
    const fullAmount = subscriptionPrice?.details?.amount || 0
    const amount = fullAmount / 100

    analytics.track('checkout_initiated', {
      planName: selectedPlan.value?.name,
      currency: subscriptionPrice?.details?.currency,
      amount,
    })

    trackEvent({
      name: 'initiate_checkout',
      data: JSON.stringify({
        id: selectedPlanItem.value?.productId,
        data: {
          PLAN: selectedPlan.value?.name,
          AMOUNT: fullAmount,
        },
      }),
    })
  })

  const handleChangeClick = () => {
    if (route.path.includes('register'))
      return router.push('/register/select-plan')
    if (route.path.includes('onboarding')) return router.push('/onboarding/pro')
    if (route.path.includes('upgrade')) return router.push('/upgrade')
    if (route.path.includes('change')) return router.push('/change-plans')
  }

  const handleBackClick = () => {
    if (route.path === '/checkout') return router.back()
    handleChangeClick()
  }

  const formatUpsellText = (text?: string) => {
    if (!text) return
    return text.replace(
      '<price>',
      selectedPlanItem.value?.upsell?.planItem.prices?.[0]?.details
        ?.renderedPrice || ''
    )
  }

  const handleUpdatePaymentMethod = () => {
    openDialog(Dialog.AddPaymentMethod)
  }

  const confirmPaymentAction = async (clientSecret: string) => {
    const stripeInstance = webviewStore.isActive
      ? null
      : stripe.value || (await getStripe())

    if (!stripeInstance) {
      errorMessage.value =
        'We could not confirm your payment. Please try again.'
      return false
    }

    const { error } = await stripeInstance.confirmCardPayment(clientSecret)

    if (error) {
      errorMessage.value = error.message || 'Payment authentication failed.'
      return false
    }

    return true
  }

  const handleUpgradeClick = () => {
    if (
      !selectedPlanItem.value?.upsell?.plan ||
      !selectedPlanItem.value?.upsell.planItem
    )
      return

    analytics.track('upsell_cta_clicked', {
      amount: (selectedPlanItem.value.prices?.[0]?.details?.amount || 0) / 100,
      currency: selectedPlanItem.value.prices?.[0]?.details?.currency,
    })

    const billingCycle =
      selectedPlanItem.value.prices[0].details?.billingCycleType || 'month'

    billingStore.setPlanData({
      plan: selectedPlanItem.value.upsell.plan,
      planItem: selectedPlanItem.value.upsell.planItem,
      billingCycle: {
        type: billingCycle,
        description:
          result.value?.plans?.groupByBillingCycle.find(
            (cycle) => cycle?.billingCycleType === billingCycle
          )?.billingCycleDescription || '',
      },
    })
  }

  const handlePurchase = async (_ev?: unknown, retry = 0) => {
    if (!selectedPlanItem.value) return

    let paymentMethodId = ''
    loading.value = true
    errorMessage.value = ''

    if (!paymentMethod.value?.last4 && stripe.value && card.value) {
      const result = await stripe.value.createPaymentMethod({
        type: 'card',
        card: card.value,
      })

      if (result.error) {
        errorMessage.value = result.error.message || 'Something went wrong.'
        loading.value = false
        return
      }
      paymentMethodId = result.paymentMethod.id
    }

    try {
      const response = await createSubscription(
        {
          platform: PlatformType.Web,
          productId: selectedPlanItem.value.productId,
          ...(paymentMethodId && { paymentMethodId }),
        },
        {
          update(cache, { data }) {
            const plan = data?.createSubscription.plan

            if (!plan) {
              refetch()
              return
            }

            if (plan.type !== PlanType.Basic) {
              userStore.markProGrantPending()
            }

            cache.updateQuery<BillingQueryQuery>(
              { query: BillingQueryDocument },
              (response) => {
                if (!response) {
                  refetch()
                  return null
                }

                const alert = response.billing.billingAlert

                return {
                  ...response,
                  billing: {
                    ...response.billing,
                    plan,
                    billingAlert:
                      alert && UPGRADE_ALERTS.includes(alert.type)
                        ? null
                        : alert,
                    upsell: null,
                  },
                }
              }
            )

            cache.updateQuery<MeQuery>({ query: MeDocument }, (response) => {
              if (!response) return
              return {
                ...response,
                me: {
                  ...response.me,
                  totalFilesLimit: selectedPlanItem.value?.maxUnits ?? null,
                  upcomingBilling: plan.details.upcomingBilling,
                  hasUploadedFileAsAPro:
                    userStore.user?.hasUploadedFileAsAPro || false,
                  canChangePlan: true,
                },
              }
            })
          },
        }
      )

      const subscription = response?.data?.createSubscription

      if (subscription?.failedMessage) {
        errorMessage.value = subscription.failedMessage
        return
      }

      let planType = subscription?.plan?.type ?? null
      let authenticated = false

      if (subscription?.requiresAction?.clientSecret) {
        const confirmed = await confirmPaymentAction(
          subscription.requiresAction.clientSecret
        )
        if (!confirmed) return

        authenticated = true
        userStore.markProGrantPending()
        planType = (await refetch())?.data.billing.plan.type ?? null
      }

      if (!planType && !authenticated) {
        errorMessage.value = 'Something went wrong'
        return
      }

      if (route.path.includes('onboarding')) {
        completeOnboarding()
        analytics.track('onboarding_completed', {
          trigger: 'checkout',
          planName: selectedPlan.value?.name,
          source: webviewStore.isActive
            ? webviewStore.isiOS
              ? 'apple'
              : 'google'
            : 'web',
        })
      }

      if (planType === PlanType.Pro) {
        openModal(Modals.WelcomeToDacrMembership)
      } else {
        openModal(Modals.WelcomeToDacrPro)
      }
    } catch (e) {
      console.error(e)
      if (retry < 2) {
        await handlePurchase(_ev, retry + 1)
      } else {
        errorMessage.value = 'Something went wrong'
      }
    } finally {
      loading.value = false
    }

    if (!errorMessage.value) {
      await router.push('/home')
    }
  }

  return {
    acceptTerms,
    loading,
    errorMessage,
    selectedPlan,
    selectedPlanItem,
    selectedBillingCycle,
    getBillingCyclePrice,
    hasMoreThanOnePrice,
    cardClasses,
    paymentMethod,
    registrationsEstimate,
    awaitingEstimate,
    handleChangeClick,
    handleBackClick,
    formatUpsellText,
    handleUpdatePaymentMethod,
    handleUpgradeClick,
    handlePurchase,
  }
}
